interface LeadWorkflowTemplateLike {
  stages_json: unknown;
  metadata_json?: Record<string, unknown> | null;
}

export interface LeadWorkflowStage {
  key: string;
  label: string;
  personas: string[];
}

export interface DerivedLeadWorkflow {
  stages: LeadWorkflowStage[];
  currentStageKey: string | null;
  currentStageLabel: string;
  currentStageNumber: number;
  totalStages: number;
  nextStageLabel: string | null;
}

const toLabel = (value: string) =>
  value
    .toLowerCase()
    .split("_")
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
    .join(" ");

const toStageLabel = (raw: unknown, fallbackKey: string) => {
  if (typeof raw === "string" && raw.trim()) return raw.trim();
  return toLabel(fallbackKey);
};

const normalizePersona = (value: string) => value.trim().toLowerCase();

const parsePersonas = (row: Record<string, unknown>) => {
  const normalized = new Set<string>();

  const addPersona = (value: unknown) => {
    if (typeof value !== "string") return;
    const persona = normalizePersona(value);
    if (persona) normalized.add(persona);
  };

  const personasRaw = row.personas;
  if (Array.isArray(personasRaw)) {
    personasRaw.forEach((value) => addPersona(value));
  } else if (typeof personasRaw === "string") {
    personasRaw
      .split(/[,/+]/)
      .map((value) => value.trim())
      .filter(Boolean)
      .forEach((value) => addPersona(value));
  }

  const personaRaw = row.persona;
  if (Array.isArray(personaRaw)) {
    personaRaw.forEach((value) => addPersona(value));
  } else {
    addPersona(personaRaw);
  }

  const visibleToRaw = row.visible_to;
  if (typeof visibleToRaw === "string" && visibleToRaw.trim()) {
    visibleToRaw
      .split(",")
      .map((value) => value.trim())
      .filter(Boolean)
      .forEach((value) => addPersona(value));
  }

  return Array.from(normalized);
};

const extractStageRows = (
  stagesJson: unknown
): Array<{ key: string; label: string; personas: string[]; order?: number; insertion: number }> => {
  const rows: Array<{
    key: string;
    label: string;
    personas: string[];
    order?: number;
    insertion: number;
  }> = [];

  let rawStages: unknown[] = [];
  if (Array.isArray(stagesJson)) {
    rawStages = stagesJson;
  } else if (
    typeof stagesJson === "object" &&
    stagesJson !== null &&
    "stages" in (stagesJson as Record<string, unknown>) &&
    Array.isArray((stagesJson as Record<string, unknown>).stages)
  ) {
    rawStages = (stagesJson as { stages: unknown[] }).stages;
  }

  rawStages.forEach((item, index) => {
    if (typeof item !== "object" || item === null) return;
    const row = item as Record<string, unknown>;
    const rawKey = row.key;
    if (typeof rawKey !== "string" || !rawKey.trim()) return;
    const key = rawKey.trim();
    const rawOrder = row.order;
    const order = typeof rawOrder === "number" ? rawOrder : undefined;
    rows.push({
      key,
      label: toStageLabel(row.label, key),
      personas: parsePersonas(row),
      order,
      insertion: index,
    });
  });

  rows.sort((a, b) => {
    if (a.order == null && b.order == null) return a.insertion - b.insertion;
    if (a.order == null) return 1;
    if (b.order == null) return -1;
    if (a.order !== b.order) return a.order - b.order;
    return a.insertion - b.insertion;
  });

  return rows;
};

const buildStatusStageMap = (
  leadStatus: string,
  stages: LeadWorkflowStage[],
  metadataJson?: Record<string, unknown> | null
) => {
  const map: Record<string, string> = {};
  const stageKeys = stages.map((stage) => stage.key);

  const overridesRaw = metadataJson?.lead_status_stage_keys;
  if (typeof overridesRaw === "object" && overridesRaw !== null) {
    Object.entries(overridesRaw as Record<string, unknown>).forEach(([rawStatus, rawStageKey]) => {
      if (typeof rawStageKey !== "string") return;
      const status = rawStatus.trim().toUpperCase();
      const stageKey = rawStageKey.trim();
      if (!status || !stageKey) return;
      if (stageKeys.includes(stageKey)) {
        map[status] = stageKey;
      }
    });
  }

  const normalizedStatus = leadStatus.trim().toUpperCase();
  return { map, normalizedStatus };
};

export const deriveLeadWorkflow = (
  leadStatus: string,
  template?: LeadWorkflowTemplateLike | null
): DerivedLeadWorkflow => {
  const stageRows = extractStageRows(template?.stages_json);
  const stages = stageRows.map((row) => ({
    key: row.key,
    label: row.label,
    personas: row.personas,
  }));

  if (stages.length === 0) {
    const fallbackLabel = toLabel(leadStatus || "UNKNOWN");
    return {
      stages: [{ key: "current", label: fallbackLabel, personas: [] }],
      currentStageKey: null,
      currentStageLabel: fallbackLabel,
      currentStageNumber: 1,
      totalStages: 1,
      nextStageLabel: null,
    };
  }

  const normalizedInputStatus = leadStatus.trim();
  const matchedStageKey =
    normalizedInputStatus
      ? stages.find(
          (stage) => stage.key.trim().toLowerCase() === normalizedInputStatus.toLowerCase()
        )?.key ?? null
      : null;

  const { map, normalizedStatus } = buildStatusStageMap(
    leadStatus,
    stages,
    template?.metadata_json
  );

  const currentStageKey =
    matchedStageKey ??
    map[normalizedStatus] ??
    stages[0]?.key ??
    null;
  const currentIndex = currentStageKey
    ? Math.max(0, stages.findIndex((stage) => stage.key === currentStageKey))
    : 0;
  const currentStageLabel = stages[currentIndex]?.label ?? toLabel(leadStatus || "UNKNOWN");
  const nextStageLabel = stages[currentIndex + 1]?.label ?? null;

  return {
    stages,
    currentStageKey,
    currentStageLabel,
    currentStageNumber: currentIndex + 1,
    totalStages: stages.length,
    nextStageLabel,
  };
};
